import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Calendar, BookOpen, Edit2, Trash2, Plus, Tag, Filter, X, ChevronDown, ChevronUp } from 'lucide-react';
import { BackButton } from './ui/BackButton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { useJournal, JournalEntry } from '@/hooks/useJournal';
import { useCustomTags } from '@/hooks/useCustomTags';
import { moodOptions } from '@/data/journalPrompts';
import { format, parseISO, isWithinInterval, startOfMonth, endOfMonth, subMonths, subDays } from 'date-fns';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { NewReflectionView } from './journal/NewReflectionView';
import { CheckInStep } from './journal/CheckInStep';

const presetTagValues = ['gratitude', 'reflection', 'dua', 'intention', 'lesson', 'outpouring'];
const presetTagLabels: Record<string, string> = {
  gratitude: 'Gratitude',
  reflection: 'Reflection',
  dua: 'Dua',
  intention: 'Intention',
  lesson: 'Lesson',
  outpouring: 'Outpouring',
};

interface JournalPageProps {
  onBack: () => void;
  onViewEntry?: (date: string) => void;
}

const getMoodEmoji = (mood: string) => {
  const option = moodOptions.find(m => m.value === mood.toLowerCase());
  return option?.emoji || '📝';
};

const getMoodLabel = (mood: string) => {
  const option = moodOptions.find(m => m.value === mood.toLowerCase());
  return option?.label || mood.charAt(0).toUpperCase() + mood.slice(1);
};

const getEntryPreview = (entry: JournalEntry, maxChars = 180): string => {
  const text = entry.reflection || entry.gratitude || entry.needsFromAllah || '';
  const cleaned = text.replace(/[#*_~`>]/g, '').trim();
  if (!cleaned) return '';
  if (cleaned.length <= maxChars) return cleaned;
  const truncated = cleaned.substring(0, maxChars);
  const lastPeriod = truncated.lastIndexOf('.');
  const lastSpace = truncated.lastIndexOf(' ');
  return truncated.substring(0, lastPeriod > 100 ? lastPeriod + 1 : lastSpace) + '…';
};

export const JournalPage = ({ onBack }: JournalPageProps) => {
  const { entries, isLoading, saveEntry, deleteEntry } = useJournal();
  const { customTags } = useCustomTags();
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState<string>('all');
  const [moodFilter, setMoodFilter] = useState<string>('all');
  const [selectedTagFilters, setSelectedTagFilters] = useState<string[]>([]);
  const [matchAllTags, setMatchAllTags] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showNewReflection, setShowNewReflection] = useState(false);
  const [showCheckIn, setShowCheckIn] = useState(false);
  const [selectedFeelings, setSelectedFeelings] = useState<string[]>([]);
  const [tagSearchQuery, setTagSearchQuery] = useState('');
  
  // Edit state - full page
  const [editingEntry, setEditingEntry] = useState<JournalEntry | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Delete confirmation state
  const [deletingEntry, setDeletingEntry] = useState<JournalEntry | null>(null);

  // All available tags
  const allAvailableTags = useMemo(() => {
    const tags = [...presetTagValues, ...customTags.map(t => t.toLowerCase())];
    return tags;
  }, [customTags]);

  // Tag counts
  const tagCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    entries.forEach(e => {
      (e.tags || []).forEach(tag => {
        const t = tag.toLowerCase();
        counts[t] = (counts[t] || 0) + 1;
      });
    });
    return counts;
  }, [entries]);

  const getTagLabel = (tag: string) => {
    if (presetTagLabels[tag]) return presetTagLabels[tag];
    const custom = customTags.find(t => t.toLowerCase() === tag);
    return custom || tag.charAt(0).toUpperCase() + tag.slice(1);
  };

  // Get unique moods for filter
  const uniqueMoods = useMemo(() => {
    const moods = new Set<string>();
    entries.forEach(e => {
      if (e.mood) moods.add(e.mood.toLowerCase());
    });
    return Array.from(moods);
  }, [entries]);

  const hasActiveFilters = dateFilter !== 'all' || moodFilter !== 'all' || selectedTagFilters.length > 0 || searchQuery.trim();

  // Filter entries
  const filteredEntries = useMemo(() => {
    let result = [...entries];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(entry =>
        entry.gratitude?.toLowerCase().includes(query) ||
        entry.reflection?.toLowerCase().includes(query) ||
        entry.needsFromAllah?.toLowerCase().includes(query) ||
        entry.mood?.toLowerCase().includes(query) ||
        entry.title?.toLowerCase().includes(query)
      );
    }

    // Date filter
    const now = new Date();
    if (dateFilter === 'last7') {
      const start = subDays(now, 7);
      result = result.filter(entry => parseISO(entry.date) >= start);
    } else if (dateFilter === 'last30') {
      const start = subDays(now, 30);
      result = result.filter(entry => parseISO(entry.date) >= start);
    } else if (dateFilter === 'thisMonth') {
      const start = startOfMonth(now);
      const end = endOfMonth(now);
      result = result.filter(entry => {
        const date = parseISO(entry.date);
        return isWithinInterval(date, { start, end });
      });
    } else if (dateFilter === 'lastMonth') {
      const lastMonth = subMonths(now, 1);
      const start = startOfMonth(lastMonth);
      const end = endOfMonth(lastMonth);
      result = result.filter(entry => {
        const date = parseISO(entry.date);
        return isWithinInterval(date, { start, end });
      });
    }

    // Mood filter
    if (moodFilter !== 'all') {
      result = result.filter(entry => entry.mood?.toLowerCase() === moodFilter);
    }

    // Tag filter
    if (selectedTagFilters.length > 0) {
      result = result.filter(entry => {
        const entryTags = (entry.tags || []).map(t => t.toLowerCase());
        if (matchAllTags) {
          return selectedTagFilters.every(f => entryTags.includes(f));
        } else {
          return selectedTagFilters.some(f => entryTags.includes(f));
        }
      });
    }

    return result;
  }, [entries, searchQuery, dateFilter, moodFilter, selectedTagFilters, matchAllTags]);

  const hasContent = (entry: JournalEntry) =>
    entry.mood || entry.gratitude || entry.reflection || entry.needsFromAllah;

  const toggleTagFilter = (tag: string) => {
    setSelectedTagFilters(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    setDateFilter('all');
    setMoodFilter('all');
    setSelectedTagFilters([]);
    setMatchAllTags(false);
  };

  const openEditEntry = (entry: JournalEntry) => {
    setEditingEntry(entry);
  };

  const handleSaveEdit = async (data: { title: string; reflection: string; tags: string[]; mood?: string; gratitude?: string; needsFromAllah?: string }) => {
    if (!editingEntry) return;
    setIsSaving(true);
    await saveEntry({
      id: editingEntry.id,
      date: editingEntry.date,
      mood: data.mood || '',
      gratitude: data.gratitude || '',
      reflection: data.reflection,
      needsFromAllah: data.needsFromAllah || '',
      title: data.title,
      tags: data.tags,
    });
    setIsSaving(false);
    setEditingEntry(null);
  };

  const handleDeleteFromEdit = async () => {
    if (!editingEntry) return;
    await deleteEntry(editingEntry.date);
    setEditingEntry(null);
  };

  const handleDelete = async () => {
    if (!deletingEntry) return;
    await deleteEntry(deletingEntry.date);
    setDeletingEntry(null);
  };

  const handleNewReflectionSave = async (data: { title: string; reflection: string; tags: string[]; mood?: string; gratitude?: string; needsFromAllah?: string }) => {
    const today = format(new Date(), 'yyyy-MM-dd');
    await saveEntry({
      date: today,
      mood: data.mood || '',
      gratitude: data.gratitude || '',
      reflection: data.reflection,
      needsFromAllah: data.needsFromAllah || '',
      title: data.title,
      tags: data.tags,
    });
    setShowNewReflection(false);
    setShowCheckIn(false);
    setSelectedFeelings([]);
  };

  const handleStartReflection = () => {
    setShowCheckIn(true);
  };

  const handleCheckInContinue = (feelings: string[]) => {
    setSelectedFeelings(feelings);
    setShowCheckIn(false);
    setShowNewReflection(true);
  };

  // Show check-in step
  if (showCheckIn) {
    return (
      <CheckInStep
        onBack={() => setShowCheckIn(false)}
        onContinue={handleCheckInContinue}
      />
    );
  }

  // Show edit view for existing entry
  if (editingEntry) {
    return (
      <NewReflectionView
        onBack={() => setEditingEntry(null)}
        onSave={handleSaveEdit}
        editEntry={editingEntry}
        onDelete={handleDeleteFromEdit}
      />
    );
  }

  // Show new reflection view
  if (showNewReflection) {
    return (
      <NewReflectionView
        onBack={() => {
          setShowNewReflection(false);
          setShowCheckIn(true);
        }}
        onSave={handleNewReflectionSave}
        initialFeelings={selectedFeelings}
      />
    );
  }

  const filteredAvailableTags = allAvailableTags.filter(t =>
    !tagSearchQuery.trim() || getTagLabel(t).toLowerCase().includes(tagSearchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-paper">
      {/* Header */}
      <div className="bg-card border-b border-border sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <BackButton onClick={onBack} srLabel="Return to dashboard" />
              <div>
                <h1 className="font-display text-2xl text-foreground">My Journal</h1>
                <p className="text-sm text-muted-foreground">
                  {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
                </p>
              </div>
            </div>
            {entries.length > 0 && (
              <Button onClick={handleStartReflection} className="gap-2">
                <Plus className="h-4 w-4" />
                New Reflection
              </Button>
            )}
          </div>

          {/* Search */}
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search reflections, gratitude, or duas..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Filter Toggle (mobile) / Inline (desktop) */}
            <div className="md:hidden">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className="gap-2 w-full justify-between"
              >
                <span className="flex items-center gap-2">
                  <Filter className="h-4 w-4" />
                  {showFilters ? 'Hide Filters' : 'Show Filters'}
                  {hasActiveFilters && (
                    <span className="w-2 h-2 rounded-full bg-primary" />
                  )}
                </span>
                {showFilters ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </div>

            {/* Filter Bar */}
            <AnimatePresence>
              {(showFilters || true) && (
                <motion.div
                  initial={false}
                  className={`space-y-3 ${showFilters ? 'block' : 'hidden md:block'}`}
                >
                  {/* Selected tag chips */}
                  {selectedTagFilters.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 items-center">
                      {selectedTagFilters.map(tag => (
                        <span
                          key={tag}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-primary/10 text-primary"
                        >
                          {getTagLabel(tag)}
                          <button onClick={() => toggleTagFilter(tag)} className="hover:text-primary/70">
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                      <div className="flex items-center gap-2 ml-2">
                        <label className="text-xs text-muted-foreground whitespace-nowrap">Match all</label>
                        <Switch
                          checked={matchAllTags}
                          onCheckedChange={setMatchAllTags}
                          className="scale-75"
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2 flex-wrap items-center">
                    {/* Date Filter */}
                    <Select value={dateFilter} onValueChange={setDateFilter}>
                      <SelectTrigger className="w-[140px]">
                        <Calendar className="h-4 w-4 mr-2" />
                        <SelectValue placeholder="Date" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Time</SelectItem>
                        <SelectItem value="last7">Last 7 Days</SelectItem>
                        <SelectItem value="last30">Last 30 Days</SelectItem>
                        <SelectItem value="thisMonth">This Month</SelectItem>
                        <SelectItem value="lastMonth">Last Month</SelectItem>
                      </SelectContent>
                    </Select>

                    {/* Mood Filter */}
                    {uniqueMoods.length > 0 && (
                      <Select value={moodFilter} onValueChange={setMoodFilter}>
                        <SelectTrigger className="w-[140px]">
                          <SelectValue placeholder="Mood" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Moods</SelectItem>
                          {uniqueMoods.map(mood => (
                            <SelectItem key={mood} value={mood}>
                              {getMoodEmoji(mood)} {mood.charAt(0).toUpperCase() + mood.slice(1)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}

                    {/* Tag Filter Popover */}
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="gap-2">
                          <Tag className="h-4 w-4" />
                          Tags
                          {selectedTagFilters.length > 0 && (
                            <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] bg-primary text-primary-foreground">
                              {selectedTagFilters.length}
                            </span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-64 p-3" align="start">
                        <div className="space-y-3">
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Filter by Tags</p>
                          {allAvailableTags.length > 6 && (
                            <Input
                              placeholder="Search tags…"
                              value={tagSearchQuery}
                              onChange={(e) => setTagSearchQuery(e.target.value)}
                              className="h-8 text-sm"
                            />
                          )}
                          <div className="space-y-1 max-h-48 overflow-y-auto">
                            {filteredAvailableTags.map(tag => (
                              <label
                                key={tag}
                                className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-accent cursor-pointer transition-colors"
                              >
                                <Checkbox
                                  checked={selectedTagFilters.includes(tag)}
                                  onCheckedChange={() => toggleTagFilter(tag)}
                                />
                                <span className="text-sm flex-1">{getTagLabel(tag)}</span>
                                {tagCounts[tag] && (
                                  <span className="text-xs text-muted-foreground">({tagCounts[tag]})</span>
                                )}
                              </label>
                            ))}
                            {filteredAvailableTags.length === 0 && (
                              <p className="text-xs text-muted-foreground py-2 text-center">No tags found</p>
                            )}
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>

                    {/* Clear All */}
                    {hasActiveFilters && (
                      <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-xs gap-1 text-muted-foreground">
                        <X className="h-3 w-3" />
                        Clear All
                      </Button>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Entry count for screen readers */}
        {!isLoading && filteredEntries.length > 0 && (
          <p className="sr-only" role="status">Showing {filteredEntries.length} {filteredEntries.length === 1 ? 'entry' : 'entries'}</p>
        )}

        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="rounded-xl border border-border/60 p-5 md:p-6 animate-pulse">
                <div className="h-4 w-48 bg-muted rounded mb-4" />
                <div className="h-5 w-64 bg-muted rounded mb-3" />
                <div className="space-y-2 mb-4">
                  <div className="h-4 w-full bg-muted rounded" />
                  <div className="h-4 w-4/5 bg-muted rounded" />
                  <div className="h-4 w-3/5 bg-muted rounded" />
                </div>
                <div className="flex gap-2">
                  <div className="h-6 w-20 bg-muted rounded-full" />
                  <div className="h-6 w-16 bg-muted rounded-full" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-cream flex items-center justify-center">
              <BookOpen className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="font-display text-xl mb-2 text-foreground">
              {entries.length === 0 ? 'No entries yet' : 'No matching entries'}
            </h3>
            <p className="text-muted-foreground max-w-sm mx-auto">
              {entries.length === 0
                ? 'Have you checked in with yourself today?'
                : 'No entries found. Try adjusting your filters.'}
            </p>
            {entries.length === 0 && (
              <p className="text-muted-foreground/70 text-sm mt-2 mb-6">
                Take a quiet moment before you begin.
              </p>
            )}
            {entries.length === 0 && (
              <Button onClick={handleStartReflection} className="gap-2">
                <Plus className="h-4 w-4" />
                Add Reflection
              </Button>
            )}
          </div>
        ) : (
          <motion.div
            className="space-y-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {filteredEntries.map((entry, index) => {
              const preview = getEntryPreview(entry);
              const visibleTags = entry.tags?.slice(0, window.innerWidth < 768 ? 2 : 3) || [];
              const extraTagCount = (entry.tags?.length || 0) - visibleTags.length;

              return (
                <motion.div
                  key={entry.id || entry.date}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  aria-label={`Journal entry from ${format(parseISO(entry.date), 'MMMM d, yyyy')}. ${preview ? preview.substring(0, 80) : 'No content'}`}
                >
                  <div className="group rounded-xl border border-border/60 bg-card p-5 md:p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_6px_20px_rgba(0,0,0,0.08)] hover:-translate-y-0.5 transition-all duration-200 cursor-pointer">
                    {/* Date + Actions */}
                    <div className="flex items-center justify-between mb-3">
                      <time className="text-[0.8125rem] font-medium text-muted-foreground tracking-wide">
                        {format(parseISO(entry.date), 'EEEE, MMMM d, yyyy')}
                      </time>
                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); openEditEntry(entry); }} className="h-8 w-8">
                          <Edit2 className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setDeletingEntry(entry); }} className="h-8 w-8 text-destructive hover:text-destructive">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>

                    {/* Title */}
                    {entry.title && (
                      <h3 className="font-display text-lg font-semibold text-foreground leading-snug mb-2 truncate">
                        {entry.title}
                      </h3>
                    )}

                    {/* Content Preview */}
                    {preview ? (
                      <p className="text-[0.9375rem] leading-relaxed text-foreground/85 italic line-clamp-3 md:line-clamp-3 mb-4">
                        "{preview}"
                      </p>
                    ) : (
                      <p className="text-sm text-muted-foreground italic mb-4">No content written…</p>
                    )}

                    {/* Footer: Tags + Mood */}
                    <div className="flex items-center justify-between gap-3">
                      {/* Tags */}
                      <div className="flex flex-wrap gap-1.5 min-w-0">
                        {visibleTags.map(tag => (
                          <span
                            key={tag}
                            className="inline-flex px-2.5 py-1 rounded-full text-[0.8125rem] bg-primary/10 border border-primary/25 text-primary/90"
                          >
                            {getTagLabel(tag.toLowerCase())}
                          </span>
                        ))}
                        {extraTagCount > 0 && (
                          <span className="inline-flex px-2 py-1 rounded-full text-[0.75rem] text-muted-foreground">
                            +{extraTagCount} more
                          </span>
                        )}
                      </div>

                      {/* Mood indicator */}
                      {entry.mood && (
                        <span className="inline-flex items-center gap-1 text-[0.8125rem] text-muted-foreground whitespace-nowrap flex-shrink-0">
                          {getMoodEmoji(entry.mood)} {getMoodLabel(entry.mood)}
                        </span>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingEntry} onOpenChange={(open) => !open && setDeletingEntry(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Journal Entry?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete your entry from {deletingEntry && format(parseISO(deletingEntry.date), 'MMMM d, yyyy')}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
